import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Eye, EyeOff } from 'lucide-react';

import AuthLayout from '../../shared/components/AuthLayout';
import Button from '../../shared/components/Button';
import Input from '../../shared/components/Input';
import LoginOption from '../../components/LoginOption';
import OverlayLoader from '../../components/Loader/OverlayLoader';
import signupImage from '../../assets/images/signup.png';
import { useAuthForm } from '../../shared/hooks/useAuthForm';
import { useSocialAuth } from '../../shared/hooks/useSocialAuth';
import { AUTH_STACK, DASHBOARD_STACK } from '../../utils/screenNames';
import {
  registration,
  getUserProfile,
  refreshTokens,
} from '../../redux/actions/authActions';
import { updateUserInfo } from '../../redux/slices/userSlice';
import { isTokenExpired } from '../../utils/helpers';

const getPasswordStrength = (password) => {
  if (!password) return 0;
  let score = 0;
  if (password.length >= 8) score++;
  if (/[a-zA-Z]/.test(password) && /\d/.test(password)) score++;
  if (/[#?!$&@%^*()_+\-=[\]{};':"\\|,.<>/~`]/.test(password)) score++;
  return score;
};

const strengthColors = ['#FF7B7B', '#FFD700', '#4AED88'];
const strengthLabels = ['Weak', 'Medium', 'Strong'];

const PasswordStrength = ({ password }) => {
  const strength = getPasswordStrength(password);

  return (
    <div className="mt-3">
      <p className="text-sm text-app-text-secondary font-proxima mb-2">
        Password strength{strength > 0 && <span className="ml-1">· {strengthLabels[strength - 1]}</span>}
      </p>
      <div className="flex gap-2">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="flex-1 h-1 rounded-full transition-all duration-300"
            style={{
              backgroundColor: i < strength ? strengthColors[Math.min(strength - 1, 2)] : '#192634',
            }}
          />
        ))}
      </div>
      <p className="text-xs text-app-text-muted font-proxima mt-2">
        At least 8 characters, 1 letter and 1 number, 1 special character (example: # ? ! $ & @)
      </p>
    </div>
  );
};

function SignupOptions() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    values,
    errors,
    handleChange,
    handleBlur,
    validateEmail,
    validatePassword,
    validate,
    setError,
  } = useAuthForm({ email: '', password: '' });
  const { signInWithProvider, isSocialLoading } = useSocialAuth((message) =>
    setError('email', message)
  );

  const [isLoading, setIsLoading] = useState(true);
  const [isSelected, setIsSelected] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  /**
   * Handle token refresh
   */
  const handleResetTokens = useCallback(
    async (refreshToken) => {
      async function onSuccess(data) {
        const { access, refresh } = data;
        window.localStorage.setItem('access', JSON.stringify(access));
        window.localStorage.setItem('refresh', JSON.stringify(refresh));

        const userData = await getUserProfile(access);
        dispatch(updateUserInfo({ userData }));
        navigate(DASHBOARD_STACK.HOME);
      }

      function onFail() {
        setIsLoading(false);
      }

      await refreshTokens(refreshToken, onSuccess, onFail);
    },
    [dispatch, navigate]
  );

  /**
   * Check if user is already logged in
   */
  useEffect(() => {
    const setUserProfile = async () => {
      const accessString = window.localStorage.getItem('access');
      const refreshString = window.localStorage.getItem('refresh');

      if (accessString) {
        const access = JSON.parse(accessString);
        if (isTokenExpired(access.expiry)) {
          const refresh = JSON.parse(refreshString);
          handleResetTokens(refresh);
        } else {
          try {
            const userData = await getUserProfile(access);
            dispatch(updateUserInfo({ userData }));
            navigate(DASHBOARD_STACK.HOME);
          } catch (err) {
            setIsLoading(false);
          }
        }
      } else {
        setIsLoading(false);
      }
    };

    setUserProfile();
  }, [navigate, dispatch, handleResetTokens]);

  /**
   * Handle Email/Password Registration
   */
  const handleRegister = async () => {
    // Validate form
    const isValid = validate({
      email: validateEmail,
      password: validatePassword,
    });

    if (!isValid) return;

    const info = { email: values.email, password: values.password };

    dispatch(
      updateUserInfo({ data: { email: values.email, password: values.password }, type: 'user' })
    );

    try {
      await registration(
        info,
        (userData) => {
          dispatch(updateUserInfo({ userData }));
          navigate(AUTH_STACK.SIGNUP_VERIFY, {
            state: { registrationInfo: { email: values.email, password: values.password }, email: values.email },
          });
        },
        (errType) => {
          if (errType === 409) {
            navigate(AUTH_STACK.SIGNUP_VERIFY, {
              state: { registrationInfo: { email: values.email, password: values.password }, email: values.email },
            });
          } else if (errType === 423) {
            setError('email', 'Email already used.');
          } else {
            setError('email', 'Registration failed.');
          }
        }
      );
    } catch (error) {
      setError('email', 'An error occurred. Please try again.');
    }
  };

  if (isLoading || isSocialLoading) {
    return <OverlayLoader />;
  }

  return (
    <div className="relative w-screen h-screen bg-app-bg">
      {/* Main Content */}
      <AuthLayout
        heroImage={signupImage}
        title={isSelected ? "Create your account": "Join Clubbie"}
        subtitle={isSelected ? "Enter your details to create your account": "Sign up for free"}
      >
        {isSelected ? (
          /* Email/Password Form */
          <>
            {/* Email Input */}
            <div className="w-full">
              <label className="block text-[#FEFCEB] text-sm font-proxima mb-2">
                Email
              </label>
              <Input
                type="email"
                name="email"
                placeholder="Email"
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!errors.email}
                errorMessage={errors.email}
              />
            </div>

            {/* Password Input */}
            <div className="w-full mt-4">
              <label className="block text-[#FEFCEB] text-sm font-proxima mb-2">
                Password
              </label>
              <Input
                type={showPassword ? 'text' : 'password'}
                name="password"
                placeholder="Password"
                value={values.password}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!errors.password}
                errorMessage={errors.password}
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-shaded-text hover:text-[#FEFCEB] transition-colors"
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                }
              />

              {/* Password Strength Indicator */}
              <PasswordStrength password={values.password} />
            </div>

            {/* Continue Button */}
            <Button
              variant="primary"
              size="lg"
              onClick={handleRegister}
              className="flex-1 w-full h-11 bg-[#00E7FF] mt-5 text-[#021529] rounded-full font-medium text-[15px] hover:opacity-50 transition-opacity"
            >
              Continue
            </Button>

            {/* Back to options */}
            <button
              onClick={() => setIsSelected(false)}
              className="w-full text-center text-sm text-app-text-secondary hover:text-light-blue transition-colors font-proxima mt-4"
            >
              Choose another sign up method
            </button>
          </>
        ) : (
          /* Social Login Options */
          <>
            <div className="w-full flex flex-col gap-3">
              <LoginOption
                optionType="email"
                optionText="Continue with email"
                onOptionClick={() => setIsSelected(true)}
              />
              <LoginOption
                optionType="google"
                optionText="Continue with Google"
                onOptionClick={() => signInWithProvider('google')}
              />
              <LoginOption
                optionType="apple"
                optionText="Continue with Apple"
                onOptionClick={() => signInWithProvider('apple')}
              />
            </div>
          </>
        )}

        {/* Login Link */}
        <p className="text-center text-sm text-app-text-secondary mt-6 font-proxima">
          Already have an account?{' '}
          <button
            onClick={() => navigate(AUTH_STACK.LOGIN_OPTIONS)}
            className="text-[#FEFCEB] underline hover:text-light-blue transition-colors"
          >
            Log in
          </button>
        </p>

        {/* Terms & Privacy */}
        <p className="text-center text-xs text-app-text-muted mt-6 font-proxima leading-relaxed">
          By continuing, you agree to our{' '}
          <a href="https://clubbie.com/service/terms-of-service" target="_blank" rel="noopener noreferrer" className="underline text-app-text hover:text-light-blue">
            Terms of Service
          </a>{' '}
          and acknowledge that you have read our{' '}
          <a href="https://clubbie.com/service/privacy-policy" target="_blank" rel="noopener noreferrer" className="underline text-app-text hover:text-light-blue">
            Privacy Policy
          </a>{' '}
          to learn how we collect, use and share your data.
        </p>
      </AuthLayout>
    </div>
  );
}

export default SignupOptions;
